import React, { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { PROJECT_STATUSES } from "@/components/EditProjectDialog";
import MultiUserSelect, { WorkflowTag } from "@/components/MultiUserSelect";
import { ChevronDown, Plus } from "lucide-react";

// Cheap in-module lookup — keeps IDs stable across the app.
const STATUS_META = Object.fromEntries(PROJECT_STATUSES.map((s) => [s.value, s]));

// Iter 45.31 — circular avatar stack for assignee cells (Projects tab
// redesign). Gradient picked by role, falling back to a stable name hash so
// the same user keeps the same color everywhere.
const ROLE_GRADIENTS = {
  content_manager: "bg-gradient-to-tr from-purple-600 to-indigo-500",
  team_lead: "bg-gradient-to-tr from-blue-600 to-cyan-500",
  writer: "bg-gradient-to-tr from-emerald-600 to-teal-500",
  publisher: "bg-gradient-to-tr from-amber-500 to-orange-500",
  editor: "bg-gradient-to-tr from-rose-500 to-pink-500",
};
const FALLBACK_GRADIENTS = Object.values(ROLE_GRADIENTS);
const ROLE_SHORT = {
  content_manager: "CM", team_lead: "TL", writer: "Writer", publisher: "Publisher",
  editor: "Editor", admin: "Admin", super_admin: "Super Admin", department_admin: "Dept Admin", hr: "HR",
};
function avatarGradient(u) {
  if (ROLE_GRADIENTS[u.role]) return ROLE_GRADIENTS[u.role];
  let h = 0;
  for (let i = 0; i < (u.name || "").length; i++) h = (h * 31 + u.name.charCodeAt(i)) >>> 0;
  return FALLBACK_GRADIENTS[h % FALLBACK_GRADIENTS.length];
}
function initials(name) {
  const parts = (name || "?").trim().split(/\s+/);
  return (parts[0]?.[0] || "?").toUpperCase() + (parts.length > 1 ? parts[parts.length - 1][0].toUpperCase() : "");
}

export function isUnassigned(p) {
  return !(p.team_lead_ids && p.team_lead_ids.length > 0)
      || !(p.content_manager_ids && p.content_manager_ids.length > 0);
}

export function InlineAssignees({ project, field, ids, userMap, users, colorClass, label, canEdit, testId, onPatch }) {
  const resolved = (ids || []).map((id) => userMap[id]).filter(Boolean);
  const shown = resolved.slice(0, 3);
  const rest = resolved.length - shown.length;
  // Iter 45.32 — avatar + name columns. Each assignee renders as a small
  // vertical stack: gradient initial avatar on top, full name underneath
  // (truncated to the column, full name in the tooltip). Editing flow is
  // unchanged: the whole group is still wrapped in MultiUserSelect.
  const chips = (
    <div className={`flex items-center justify-center min-h-[28px] px-1 py-0.5 rounded ${canEdit ? "cursor-pointer hover:bg-slate-100" : ""}`} title={canEdit ? `Click to edit ${label}` : resolved.map((u) => u.name).join(", ")} data-testid={testId}>
      {resolved.length === 0 ? (
        canEdit ? (
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-dashed border-slate-300 text-slate-400 hover:border-purple-300 hover:text-purple-500 transition" title={`Add ${label}`}>
            <Plus className="h-3 w-3" />
          </span>
        ) : <span className="text-slate-300 text-xs italic">—</span>
      ) : (
        <div className="flex items-start gap-2.5">
          {shown.map((u) => (
            <div key={u.id} className="flex flex-col items-center min-w-0" data-testid={`${testId}-avatar-${u.id}`}>
              <span
                title={`${u.name} • ${ROLE_SHORT[u.role] || u.role}${u.workflow_type ? ` • ${u.workflow_type}` : ""}`}
                className={`relative inline-flex h-7 w-7 items-center justify-center rounded-full ring-2 ring-white shadow-sm text-[11px] font-bold text-white tracking-wide transition-transform hover:scale-110 hover:z-20 ${avatarGradient(u)}`}
              >
                {initials(u.name)}
              </span>
              {/* Iter 45.33 — full name, never truncated */}
              <span className="mt-0.5 text-[10px] leading-tight text-slate-600 font-medium whitespace-nowrap text-center" title={u.name}>
                {u.name}
              </span>
            </div>
          ))}
          {rest > 0 && (
            <div className="flex flex-col items-center min-w-0">
              <span
                title={resolved.slice(3).map((u) => u.name).join(", ")}
                className="relative inline-flex h-7 w-7 items-center justify-center rounded-full bg-slate-100 border border-slate-200 ring-2 ring-white text-[10px] font-bold text-slate-600"
              >
                +{rest}
              </span>
              <span className="mt-0.5 text-[10px] leading-tight text-slate-400 font-medium whitespace-nowrap">more</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
  if (!canEdit) return chips;
  return (
    <MultiUserSelect
      value={ids || []}
      onChange={(next) => onPatch(project, { [field]: next })}
      users={users}
      variant="trigger"
      testId={`${testId}-picker`}
    >{chips}</MultiUserSelect>
  );
}

export function InlineStatusPill({ project, canEdit, onPatch }) {
  const meta = STATUS_META[project.status || "to_start"] || STATUS_META["to_start"];
  const pill = (
    <span className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-0.5 rounded-full border ${meta.chip} ${canEdit ? "cursor-pointer hover:brightness-95" : ""}`} data-testid={`status-pill-${project.id}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />
      {meta.label}
      {canEdit && <ChevronDown className="h-3 w-3 opacity-60 -mr-0.5" />}
    </span>
  );
  if (!canEdit) return pill;
  return (
    <Popover>
      <PopoverTrigger asChild><button type="button">{pill}</button></PopoverTrigger>
      <PopoverContent align="start" className="w-44 p-1">
        {PROJECT_STATUSES.map((s) => (
          <button
            key={s.value}
            onClick={() => onPatch(project, { status: s.value })}
            className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm hover:bg-slate-50 text-left ${(project.status || "to_start") === s.value ? "bg-purple-50/60 font-medium" : ""}`}
            data-testid={`status-opt-${project.id}-${s.value}`}
          >
            <span className={`h-2 w-2 rounded-full ${s.dot}`} />
            {s.label}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

export function InlineStatusNote({ project, canEdit, onPatch }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(project.status_note || "");
  useEffect(() => { setVal(project.status_note || ""); }, [project.status_note]);
  if (!canEdit) return <span className="text-xs text-slate-600 max-w-[200px] truncate block">{project.status_note || <span className="text-slate-300">—</span>}</span>;
  if (editing) {
    return (
      <Input
        autoFocus
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onBlur={async () => { setEditing(false); if (val !== (project.status_note || "")) await onPatch(project, { status_note: val }); }}
        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") { setVal(project.status_note || ""); setEditing(false); } }}
        className="h-7 text-xs px-2"
        data-testid={`status-note-input-${project.id}`}
      />
    );
  }
  return (
    <span
      onClick={() => setEditing(true)}
      className="text-xs text-slate-600 max-w-[200px] truncate block cursor-pointer hover:bg-slate-100 rounded px-1 py-0.5 min-h-[22px]"
      title="Click to edit note"
      data-testid={`status-note-${project.id}`}
    >
      {project.status_note || <span className="text-slate-400 italic">+ add note</span>}
    </span>
  );
}

export { STATUS_META };
